import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { EneaRegistroModal } from "./EneaRegistro";
import { Menu, X, ChevronDown, Lock, ShoppingCart, User, Download, Smartphone, Ruler } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { brand, nav, productCategories } from "../mock/content";
import TorriLogo from "./TorriLogo";
import EuIcon from "./EuIcon";
import { useConfigModal, withPartnerParams } from "./ConfigModal";
import LangSwitcher from "./LangSwitcher";
import { langFromPath, localizePath, stripLang } from "../lib/locale";
import { t } from "../lib/ui";
import { useCart } from "../lib/cart";
import { vestaVisible, withAi } from "../lib/vestaLaunch";
import { CartDrawer } from "./CartDrawer";
const CatalogFallbackGen = React.lazy(() => import("./CatalogFallbackGen"));
const COMPANY_PROFILE = "/docs/torri-presentazione-aziendale.pdf";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [eneaOpen, setEneaOpen] = useState(false);
  const openEnea = (e) => { e.stopPropagation(); setOpen(false); setEneaOpen(true); };
  const [openSub, setOpenSub] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [shopCats, setShopCats] = useState(() => new Set());
  const [me, setMe] = useState(null);
  const [clientAuthed, setClientAuthed] = useState(false);
  const [fastLoading, setFastLoading] = useState(false);
  const cartItems = useCart();
  const cartCount = cartItems.reduce((s, x) => s + x.qty, 0);
  const { openConfigModal } = useConfigModal();
  const [fallback, setFallback] = useState(null);
  const [catState, setCatState] = useState({ generating: false, eta: null });
  const catLoading = catState.generating;
  const catEta = catState.eta;
  const downloadCatalog = async () => {
    setOpen(false);
    if (!me && !clientAuthed) {
      const lang = langFromPath(location.pathname);
      navigate(localizePath("/area-clienti", lang));
      return;
    }
    if (catLoading || fastLoading) return;
    // Percorso veloce: usa il PDF base pre-generato (download quasi istantaneo).
    setFastLoading(true);
    try {
      const { downloadBaseCatalog } = await import("../lib/catalogBase");
      await downloadBaseCatalog(me, "TORRI-Catalogo-Completo.pdf", langFromPath(location.pathname));
    } catch (e) {
      // Fallback: nessun catalogo base caricato ancora → generazione client-side.
      setFallback({ client: me, k: Date.now() });
    } finally {
      setFastLoading(false);
    }
  };
  const navigate = useNavigate();
  const location = useLocation();
  const isIt = langFromPath(location.pathname) === "it";  // e-commerce solo in IT

  useEffect(() => {
    if (!isIt) { setShopCats(new Set()); return; }
    fetch(`${API}/api/shop/products`)
      .then((r) => r.json())
      .then((list) => { if (Array.isArray(list)) setShopCats(new Set(list.map((p) => p.cat).filter(Boolean))); })
      .catch(() => {});
  }, [isIt]);

  // true se la categoria (dal suo href) ha almeno un prodotto acquistabile online
  const isShoppable = (href) => {
    if (!isIt || shopCats.size === 0) return false;
    const slug = (href || "").split("/").pop();
    const pc = productCategories.find((c) => c.slug === slug);
    return !!(pc && shopCats.has(pc.cat));
  };

  // Configuratore Sistemi di Fissaggio in arrivo
  const isConfigComing = (href) => (href || "").split("/").pop() === "sistemi-di-fissaggio";

  // Etichetta Registro ENEA sui Pannelli Fotovoltaici (solo IT)
  const isInverterCat = (href) => (href || "").split("/").pop() === "inverter";
  const monitorBadge = (href, mobile) => isInverterCat(href) && (
    <span
      role="link"
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); go(`${href}#monitoraggio`); }}
      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); go(`${href}#monitoraggio`); } }}
      data-testid={mobile ? "nav-mobile-monitoring-badge" : "nav-monitoring-badge"}
      className={`inline-flex items-center gap-1 rounded font-bold shrink-0 whitespace-nowrap cursor-pointer hover:bg-[#F9E212] hover:text-black transition-colors ${mobile ? "px-2 py-0.5 text-[11px]" : "px-1.5 py-0.5 text-[11px] tracking-normal"}`}
      style={{ border: `1px solid ${brand.yellow}`, color: brand.yellow, fontFamily: "Space Grotesk, sans-serif" }}
    >
      <Smartphone size={mobile ? 11 : 10} /> {t("nav_monitoring_app")}
    </span>
  );

  const ROOF_TOOL = "https://configuratore.torri.solar/free/tetto";
  const openRoof = (e) => { e.stopPropagation(); setOpen(false); window.open(withPartnerParams(ROOF_TOOL), "_blank", "noopener,noreferrer"); };
  const roofBadge = (href, mobile) => isConfigComing(href) && (
    <span
      role="link"
      tabIndex={0}
      onClick={openRoof}
      onKeyDown={(e) => { if (e.key === "Enter") openRoof(e); }}
      data-testid={mobile ? "nav-mobile-config-badge-sistemi-di-fissaggio" : "nav-config-badge-sistemi-di-fissaggio"}
      className={`inline-flex items-center gap-1 rounded font-bold shrink-0 whitespace-nowrap cursor-pointer hover:bg-[#F9E212] hover:text-black transition-colors ${mobile ? "px-2 py-0.5 text-[11px]" : "px-1.5 py-0.5 text-[11px] tracking-normal"}`}
      style={{ border: `1px solid ${brand.yellow}`, color: brand.yellow, fontFamily: "Space Grotesk, sans-serif" }}
    >
      <Ruler size={mobile ? 11 : 10} /> {t("nav_config_coming").toUpperCase()}
    </span>
  );

  const isEnea = (href) => isIt && (href || "").split("/").pop() === "pannelli-fotovoltaici";

  useEffect(() => {
    const onOpenCart = () => setCartOpen(true);
    window.addEventListener("open-cart", onOpenCart);
    return () => window.removeEventListener("open-cart", onOpenCart);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const load = () => {
      const tk = localStorage.getItem("torri_client_token") || "";
      if (!tk) { setMe(null); setClientAuthed(false); return; }
      fetch(`${API}/api/portal/auth/me`, { headers: { Authorization: `Bearer ${tk}` } })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (d) { setMe(d); setClientAuthed(true); }
          else {
            setMe(null);
            fetch(`${API}/api/client/me`, { headers: { Authorization: `Bearer ${tk}` } })
              .then((r2) => setClientAuthed(r2.ok))
              .catch(() => setClientAuthed(false));
          }
        })
        .catch(() => { setMe(null); setClientAuthed(false); });
    };
    load();
    window.addEventListener("focus", load);
    window.addEventListener("storage", load);
    window.addEventListener("torri-auth-changed", load);
    return () => {
      window.removeEventListener("focus", load);
      window.removeEventListener("storage", load);
      window.removeEventListener("torri-auth-changed", load);
    };
  }, []);

  const loggedName = me ? (me.company || me.name || (me.email || "").split("@")[0] || "") : "";

  const go = (href) => {
    setOpen(false);
    const lang = langFromPath(location.pathname);
    if (href.startsWith("/")) {
      if (href.includes("#")) {
        const [path, hash] = href.split("#");
        navigate(localizePath(path, lang) + (hash === "tech-ai-vesta" ? `#${hash}` : ""));
        let tries = 0;
        const seek = () => {
          const el = document.getElementById(hash);
          if (el) el.scrollIntoView({ behavior: "smooth" });
          else if (tries++ < 15) setTimeout(seek, 200);
        };
        setTimeout(seek, 300);
      } else {
        navigate(localizePath(href, lang));
      }
      return;
    }
    // anchor: navigate home first if on another page
    if (stripLang(location.pathname) !== "/") {
      navigate(localizePath("/", lang));
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 350);
    } else {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const goHome = (e) => {
    e.preventDefault();
    setOpen(false);
    const lang = langFromPath(location.pathname);
    if (stripLang(location.pathname) !== "/") navigate(localizePath("/", lang));
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled ? "bg-black/90 backdrop-blur-md border-b border-white/10" : "bg-transparent"
        }`}
      >
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-10 h-[76px] flex items-center justify-between gap-4">
          {/* Logo */}
          <a href={localizePath("/", langFromPath(location.pathname))} onClick={goHome} className="group flex items-center shrink-0">
            <TorriLogo height={46} onDark className="transition-transform group-hover:scale-[1.02] shrink-0 max-w-none" />
          </a>

          {/* Badge accesso cliente (solo mobile/tablet, zona centrale) */}
          {me && (
            <a
              href="/area-clienti"
              data-testid="header-mobile-account"
              title={loggedName}
              className="xl:hidden absolute left-1/2 -translate-x-1/2 max-w-[44%] flex items-center gap-1.5 px-2.5 py-1 rounded-full border bg-black/45 backdrop-blur-sm"
              style={{ borderColor: me.is_partner ? brand.yellow : "rgba(255,255,255,0.22)" }}
            >
              {me.is_partner && me.has_logo && me.user_id && (
                <img src={`${API}/api/portal/logo/${me.user_id}`} alt="" data-testid="header-mobile-account-logo" className="h-5 w-5 rounded-full object-contain bg-white p-[1px] shrink-0" />
              )}
              <span className="min-w-0 truncate text-[11px] leading-tight" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
                <span className="font-semibold" style={{ color: me.is_partner ? brand.yellow : "rgba(255,255,255,0.7)" }}>{(me.is_partner ? t("hdr_partner") : t("hdr_access"))}: </span>
                <span className="text-white" data-testid="header-mobile-account-name">{loggedName}</span>
              </span>
            </a>
          )}

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-5 2xl:gap-6">
            {nav.map((n) =>
              n.children ? (
                <div key={n.label} className="relative group">
                  <button
                    onClick={() => go(n.href)}
                    className="whitespace-nowrap text-[12px] xl:text-[13px] 2xl:text-[14px] uppercase tracking-[0.1em] text-white/80 hover:text-white transition-colors relative"
                    style={{ fontFamily: "Space Grotesk, sans-serif" }}
                  >
                    {n.label}
                    <span className="absolute -bottom-1.5 left-0 w-0 h-[2px] group-hover:w-full transition-all duration-300" style={{ background: brand.yellow }} />
                  </button>
                  <div className="absolute left-0 top-full pt-4 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
                    <div className="min-w-[190px] bg-black/95 backdrop-blur-md border border-white/10 py-2">
                      {n.children.filter(vestaVisible).map((c) => (
                        <button
                          key={c.label}
                          onClick={() => go(c.href)}
                          data-testid={`nav-sub-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className="flex items-center justify-between gap-2 w-full text-left px-5 py-2.5 text-[12px] uppercase tracking-[0.1em] text-white/70 hover:text-white hover:bg-white/[0.06] transition-colors"
                          style={{ fontFamily: "Space Grotesk, sans-serif" }}
                        >
                          <span>{c.vesta ? withAi(c.label) : c.label}</span>
                          {isShoppable(c.href) && (
                            <span data-testid={`nav-shop-badge-${c.href.split("/").pop()}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-bold tracking-normal text-black shrink-0" style={{ background: brand.yellow }}>
                              <ShoppingCart size={10} /> ONLINE
                            </span>
                          )}
                          {roofBadge(c.href, false)}
                          {monitorBadge(c.href, false)}
                          {isEnea(c.href) && (
                            <span role="button" tabIndex={0} onClick={openEnea} onKeyDown={(e) => { if (e.key === "Enter") openEnea(e); }} data-testid="nav-enea-badge-pannelli-fotovoltaici" className="cursor-pointer hover:bg-[#F9E212] hover:text-black transition-colors inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-bold tracking-normal shrink-0 whitespace-nowrap" style={{ border: `1px solid ${brand.yellow}`, color: brand.yellow }}>
                              <EuIcon size={10} /> REGISTRO ENEA
                            </span>
                          )}
                        </button>
                      ))}
                      {n.href === "/azienda" && (
                        <a
                          href={COMPANY_PROFILE}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="nav-company-profile"
                          className="flex items-center gap-2 w-full whitespace-nowrap text-left px-5 py-2.5 mt-1 border-t border-white/10 text-[12px] uppercase tracking-[0.1em] font-semibold hover:bg-white/[0.06] transition-colors"
                          style={{ fontFamily: "Space Grotesk, sans-serif", color: brand.yellow }}
                        >
                          <Download size={13} /> {t("nav_company_profile")}
                        </a>
                      )}
                      {n.href === "/prodotti" && (
                        <button
                          onClick={downloadCatalog}
                          disabled={catLoading || fastLoading}
                          data-testid="nav-download-catalog"
                          className="flex items-center gap-2 w-full text-left px-5 py-2.5 mt-1 border-t border-white/10 text-[12px] uppercase tracking-[0.1em] font-semibold hover:bg-white/[0.06] transition-colors disabled:opacity-50"
                          style={{ fontFamily: "Space Grotesk, sans-serif", color: brand.yellow }}
                        >
                          {(me || clientAuthed) ? <Download size={13} /> : <Lock size={13} />} {fastLoading ? t("nav_catalog_preparing") : catLoading ? `${t("nav_catalog_generating")} ~${catEta ?? 60}s` : t("nav_download_catalog")}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  key={n.label}
                  onClick={() => go(n.href)}
                  className="whitespace-nowrap text-[12px] xl:text-[13px] 2xl:text-[14px] uppercase tracking-[0.1em] text-white/80 hover:text-white transition-colors relative group"
                  style={{ fontFamily: "Space Grotesk, sans-serif" }}
                >
                  {n.label}
                  <span className="absolute -bottom-1.5 left-0 w-0 h-[2px] group-hover:w-full transition-all duration-300" style={{ background: brand.yellow }} />
                </button>
              )
            )}
          </nav>

          <div className="flex items-center gap-4 shrink-0">
            <div className="hidden xl:block">
              <LangSwitcher />
            </div>
            <button
              type="button"
              onClick={() => setCartOpen(true)}
              data-testid="header-cart-btn"
              aria-label="Carrello"
              className={`relative text-white/90 hover:text-[#F9E212] transition-colors p-[11px] -m-[7px] ${isIt ? "" : "hidden"}`}
            >
              <ShoppingCart size={22} />
              {cartCount > 0 && (
                <span data-testid="header-cart-count" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[11px] font-black text-black rounded-full" style={{ background: brand.yellow }}>{cartCount}</span>
              )}
            </button>
            {me ? (
              <a
                href="/area-clienti"
                data-testid="header-account-btn"
                title={loggedName}
                className="hidden xl:inline-flex items-center gap-2 pl-2 pr-4 py-1.5 border text-[12px] uppercase tracking-[0.12em] font-semibold transition-[background-color,box-shadow,border-color] duration-300 hover:bg-[#F9E212]/10 hover:shadow-[0_0_18px_rgba(249, 226, 18,0.32)] group"
                style={{ borderColor: brand.yellow, color: brand.yellow, fontFamily: "Space Grotesk, sans-serif" }}
              >
                {me.has_logo && me.user_id ? (
                  <img src={`${API}/api/portal/logo/${me.user_id}`} alt="" data-testid="header-account-logo" className="h-7 w-7 rounded-full object-contain bg-white p-0.5 shrink-0" />
                ) : (
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#F9E212] text-black shrink-0"><User size={15} /></span>
                )}
                <span className="max-w-[130px] truncate normal-case tracking-normal" data-testid="header-account-name">{loggedName}</span>
              </a>
            ) : (
              <a
                href="/area-clienti"
                data-testid="header-area-clienti-btn"
                className="hidden xl:inline-flex items-center gap-2 px-4 py-2.5 border text-[12px] uppercase tracking-[0.14em] font-semibold transition-[background-color,box-shadow,border-color,letter-spacing] duration-300 hover:bg-[#F9E212]/10 hover:shadow-[0_0_18px_rgba(249, 226, 18,0.32)] hover:tracking-[0.2em] whitespace-nowrap"
                style={{ borderColor: brand.yellow, color: brand.yellow, fontFamily: "Space Grotesk, sans-serif" }}
              >
                <Lock size={13} /> {t("area_clienti")}
              </a>
            )}
            <button
              type="button"
              onClick={openConfigModal}
              data-testid="header-configura-btn"
              className="hidden sm:inline-flex items-center px-5 py-2.5 text-[12px] uppercase tracking-[0.14em] font-semibold text-black transition-transform hover:scale-[1.03]"
              style={{ background: brand.yellow, fontFamily: "Space Grotesk, sans-serif" }}
            >
              {t("cta_configure_short")}
            </button>
            <button className="xl:hidden text-white p-[11px] -m-[7px]" onClick={() => setOpen(true)} aria-label="Apri menu">
              <Menu size={26} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div className={`fixed inset-0 z-[60] xl:hidden overflow-hidden transition-all duration-400 ${open ? "visible" : "invisible"}`}>
        <div className={`absolute inset-0 bg-black/70 transition-opacity ${open ? "opacity-100" : "opacity-0"}`} onClick={() => setOpen(false)} />
        <div className={`absolute right-0 top-0 h-full w-[82%] max-w-[360px] bg-black border-l border-white/10 p-8 transition-transform duration-400 ${open ? "translate-x-0" : "translate-x-full"}`}>
          <div className="flex items-center justify-between mb-12">
            <TorriLogo height={34} onDark />
            <button onClick={() => setOpen(false)} className="text-white" aria-label="Chiudi menu"><X size={26} /></button>
          </div>
          <nav className="flex flex-col gap-1">
            {nav.map((n) => {
              const hasChildren = !!n.children;
              const isOpen = openSub === n.label;
              const slug = n.label.toLowerCase().replace(/\s+/g, "-");
              return (
                <div key={n.label}>
                  <div className="w-full flex items-center justify-between border-b border-white/10">
                    <button
                      onClick={() => go(n.href)}
                      data-testid={`nav-mobile-${slug}`}
                      className="flex-1 text-left text-white/90 text-xl py-3 hover:text-[#F9E212] transition-colors"
                      style={{ fontFamily: "Archivo" }}
                    >
                      {n.label}
                    </button>
                    {hasChildren && (
                      <button
                        onClick={() => setOpenSub(isOpen ? null : n.label)}
                        aria-expanded={isOpen}
                        aria-label={`Espandi ${n.label}`}
                        data-testid={`nav-mobile-toggle-${slug}`}
                        className="p-3 -mr-1"
                      >
                        <ChevronDown size={20} className={`transition-transform duration-300 ${isOpen ? "rotate-180 text-[#F9E212]" : "text-white/50"}`} />
                      </button>
                    )}
                  </div>
                  {hasChildren && (
                    <div className={`overflow-hidden transition-all duration-300 ease-out ${isOpen ? "max-h-[420px] opacity-100" : "max-h-0 opacity-0"}`}>
                      <div className="flex flex-col">
                        {n.children.filter(vestaVisible).map((c) => (
                          <button
                            key={c.label}
                            onClick={() => go(c.href)}
                            data-testid={`nav-mobile-sub-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="flex items-center gap-2 text-left text-white/55 text-base py-2.5 pl-4 border-b border-white/5 hover:text-[#F9E212] transition-colors"
                            style={{ fontFamily: "Archivo" }}
                          >
                            <span>{c.label}</span>
                            {isShoppable(c.href) && (
                              <span data-testid={`nav-mobile-shop-badge-${c.href.split("/").pop()}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold text-black shrink-0" style={{ background: brand.yellow, fontFamily: "Space Grotesk, sans-serif" }}>
                                <ShoppingCart size={11} /> ONLINE
                              </span>
                            )}
                            {roofBadge(c.href, true)}
                            {monitorBadge(c.href, true)}
                            {isEnea(c.href) && (
                              <span role="button" tabIndex={0} onClick={openEnea} onKeyDown={(e) => { if (e.key === "Enter") openEnea(e); }} data-testid="nav-mobile-enea-badge-pannelli-fotovoltaici" className="cursor-pointer hover:bg-[#F9E212] hover:text-black transition-colors inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold shrink-0 whitespace-nowrap" style={{ border: `1px solid ${brand.yellow}`, color: brand.yellow, fontFamily: "Space Grotesk, sans-serif" }}>
                                <EuIcon size={11} /> REGISTRO ENEA
                              </span>
                            )}
                          </button>
                        ))}
                        {n.href === "/azienda" && (
                          <a
                            href={COMPANY_PROFILE}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setOpen(false)}
                            data-testid="nav-mobile-company-profile"
                            className="flex items-center gap-2 text-left text-base py-2.5 pl-4 border-b border-white/5 font-semibold transition-colors"
                            style={{ fontFamily: "Archivo", color: "#F9E212" }}
                          >
                            <Download size={16} /> {t("nav_company_profile")}
                          </a>
                        )}
                        {n.href === "/prodotti" && (
                          <button
                            onClick={downloadCatalog}
                            disabled={catLoading || fastLoading}
                            data-testid="nav-mobile-download-catalog"
                            className="flex items-center gap-2 text-left text-base py-2.5 pl-4 border-b border-white/5 font-semibold transition-colors disabled:opacity-50"
                            style={{ fontFamily: "Archivo", color: "#F9E212" }}
                          >
                            {(me || clientAuthed) ? <Download size={16} /> : <Lock size={16} />} {fastLoading ? t("nav_catalog_preparing") : catLoading ? `${t("nav_catalog_generating")} ~${catEta ?? 60}s` : t("nav_download_catalog")}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
          <button
            type="button"
            onClick={() => { setOpen(false); openConfigModal(); }}
            data-testid="header-mobile-configura-btn"
            className="mt-8 inline-flex w-full justify-center items-center py-4 text-sm uppercase tracking-[0.14em] font-semibold text-black"
            style={{ background: brand.yellow }}
          >
            {t("cta_configure")}
          </button>
          {me ? (
            <a
              href="/area-clienti"
              data-testid="header-mobile-account-btn"
              onClick={() => setOpen(false)}
              className="mt-4 inline-flex w-full justify-center items-center gap-3 py-3.5 border text-sm uppercase tracking-[0.1em] font-semibold"
              style={{ borderColor: brand.yellow, color: brand.yellow }}
            >
              {me.has_logo && me.user_id ? (
                <img src={`${API}/api/portal/logo/${me.user_id}`} alt="" data-testid="header-mobile-account-logo" className="h-8 w-8 rounded-full object-contain bg-white p-0.5 shrink-0" />
              ) : (
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#F9E212] text-black shrink-0"><User size={16} /></span>
              )}
              <span className="flex flex-col items-start leading-tight normal-case tracking-normal min-w-0">
                <span className="text-[11px] uppercase tracking-[0.12em] text-[#F9E212]/70">Accesso effettuato</span>
                <span className="text-white truncate max-w-[190px]" data-testid="header-mobile-account-name">{loggedName}</span>
              </span>
            </a>
          ) : (
            <a
              href="/area-clienti"
              data-testid="header-mobile-area-clienti-btn"
              onClick={() => setOpen(false)}
              className="mt-4 inline-flex w-full justify-center items-center gap-2 py-4 border text-sm uppercase tracking-[0.14em] font-semibold"
              style={{ borderColor: brand.yellow, color: brand.yellow }}
            >
              <Lock size={15} /> {t("area_clienti")}
            </a>
          )}
          <LangSwitcher variant="mobile" />
        </div>
      </div>
      {isIt && <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />}
      {fallback && (
        <React.Suspense fallback={null}>
          <CatalogFallbackGen key={fallback.k} client={fallback.client} onState={setCatState} />
        </React.Suspense>
      )}
      {eneaOpen && createPortal(<EneaRegistroModal open={eneaOpen} onClose={() => setEneaOpen(false)} />, document.body)}
    </>
  );
}
